import { Box, Button, Divider, Tooltip, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import { ExpenseProfileService } from "subModule/src/core/services";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { AddExpenseResponse, Expenses } from "subModule/src/services/expense/interface";
import {
  extractMediaFileName,
  fetchMediaAsDataUrlMap,
  isImageFileName,
} from "Helpers/mediaView";
import MediaImage from "common/MediaImage/MediaImage";

const ViewExpense: React.FC = () => {
    const params = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(true);
    const [expense, setExpense] = useState<Expenses>();
    const [attachmentPreviews, setAttachmentPreviews] = useState<Record<string, string>>({});
    useEffect(() => {
        if (loading) getAdjustedInvoices();
    }, []);
    const getAdjustedInvoices = () => {
        ExpenseProfileService.getExpensesById(Number(params?.id)).then(async (res: AddExpenseResponse) => {
            if (res.success) {
                setExpense(res?.data || {});
                const urls = Array.isArray(res?.data?.url) ? res.data.url : [];
                const previews = await fetchMediaAsDataUrlMap(urls);
                setAttachmentPreviews(previews);
                setLoading(false);
            } else {
                setLoading(false);
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const amount = expense?.amount || 0;
    const taxPercentage = expense?.taxPercentage ?? expense?.taxPercentageValue ?? 0;
    const taxValue = (amount * taxPercentage) / 100;
    const amountWithTax = amount + taxValue;
    const formatAmount = (value: number) => new Intl.NumberFormat('en-US', {}).format(value);
    return (
        loading ? <Loader /> : <Box>
            <Box className='max-w-[1100px] bg-white py-[10px] px-[20px] rounded-[8px] block mx-auto hide-on-print mb-4'>
                <Box className='flex justify-between items-center' id='form'>
                    <Typography className="font-Saira text-[12px] font-semibold" style={{ color: themeColors?.primary }}>{t('expense')}</Typography>
                    <Box className='flex gap-2'>
                        <Button className="secondary" variant="contained" style={{ color: themeColors?.primary }} disableElevation onClick={() => navigate('/expenses')}>{t('back')}</Button>
                        <Button className="primary" variant="contained" style={{ background: themeColors?.primary }} disableElevation onClick={() => window.print()}>{t('Print')}</Button>
                    </Box>
                </Box>
            </Box>
            <Box id="print-root" className='max-w-[1100px] bg-white p-[20px] rounded-[8px] block mx-auto'>
                <Box className='flex justify-between mb-2 items-center'>
                    <Typography className="font-Saira text-[12px] text-left">
                        <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('title')}: </span>
                        <span className="font-semibold">{expense?.title || ''}</span>
                    </Typography>
                    {expense?.referenceNumber ? <Box className='block py-1 px-2 rounded-[8px]' style={{ background: themeColors?.appBg }}>
                        <Typography className="font-Saira text-[12px] text-end font-semibold">
                            <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('referenceNo')} </span>
                            <span className="font-semibold">{expense?.referenceNumber || ''}</span>
                        </Typography>
                    </Box> : null}
                </Box>
                <Divider className="bg-[#d9d9d9] mt-3 mb-2" />
                <Box className='flex justify-between items-baseline mt-2'>
                    <Box className='flex flex-col gap-1'>
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('vendor')}: </span>
                            <span className="font-bold">{expense?.supplierTitle || ''}</span>
                        </Typography>
                        {expense?.accountTitle ? <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('ACCOUNT')}: </span>
                            <span className="font-bold">{expense?.accountTitle || ''}</span>
                        </Typography> : ''}
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('station')}: </span>
                            <span className="font-bold">{expense?.stationTitle || ''}</span>
                        </Typography>
                    </Box>
                    <Box className="flex flex-col gap-1 p-2 rounded-[8px]" style={{ background: themeColors?.appBg }}>
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('paymentMode')}: </span>
                            <span className="font-bold">{expense?.paymentModeTitle || ''}</span>
                        </Typography>
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('expenseType')}</span>
                            <span> : </span>
                            <span className="font-bold">{expense?.typeTitle || ''}
                            </span>
                        </Typography>
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('expense') + ' ' + t('Date')}</span>
                            <span> : </span>
                            <span className="font-bold">{expense?.date?.split('T')[0] || ''}</span>
                        </Typography>
                        {expense?.refDate ? <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('recipentDate')}</span>
                            <span> : </span>
                            <span className="font-bold">{expense?.refDate?.split('T')[0] || ''}</span>
                        </Typography> : null}
                    </Box>
                </Box>
                <Divider className="bg-[#d9d9d9] mt-3 mb-2" />
                <Box className={`flex my-2 ${expense?.description ? 'justify-between' : 'justify-end'}`}>
                    {expense?.description ? <Box className="w-[70%]">
                        <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">{t("remarks")}</Typography>
                        <Typography className="font-Saira text-[12px] text-[#171826] text-left">{expense?.description}</Typography>
                    </Box> : null}
                    <Box className="flex flex-col gap-1 items-end">
                        <Typography className="font-Saira text-[12px] text-left flex">
                            <span className="font-medium">{t('amount')}: </span>
                            <p className="font-bold ms-1">
                                <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                <span className="italic font-bold ms-1">{formatAmount(amount)}</span>
                            </p>
                        </Typography>
                        <Typography className="font-Saira text-[12px] text-left flex">
                            <span className="font-medium">{t('tax')} ({taxPercentage}%): </span>
                            <p className="font-bold ms-1">
                                <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                <span className="italic font-bold ms-1">{formatAmount(taxValue)}</span>
                            </p>
                        </Typography>
                        <Typography className="font-Saira text-[12px] text-left flex">
                            <span className="font-medium">{t('amountWithTax')}: </span>
                            <p className="font-bold ms-1">
                                <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                <span className="italic font-bold ms-1">{formatAmount(amountWithTax)}</span>
                            </p>
                        </Typography>
                    </Box>
                </Box>
                {(Array.isArray(expense?.url) && expense?.url?.length) ? <Box>
                    <Divider className="bg-[#d9d9d9] mt-3 mb-2" />
                    <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">{t("Attachments")}</Typography>
                    <Box className='flex gap-1 flex-wrap'>
                        {Array.isArray(expense?.url) && ((expense?.url || [])?.map((u: string, i: number) => (
                            <Box className='relative' key={i}>
                                <Box className='cursor-pointer inline-flex gap-1 items-center'>
                                    {isImageFileName(u) && attachmentPreviews[u] ? (
                                      <MediaImage src={attachmentPreviews[u]} />
                                    ) : <Tooltip title={u || ''} placement="top" enterDelay={0}>
                                            <span 
                                                className='font-Saira text-[11px] font-semibold text-left rounded-[25px] py-[7px] px-[12px] text-ellipsis overflow-hidden w-[210px] whitespace-nowrap' 
                                                onClick={() => window.open(u, '_blank')}
                                                style={{ background: themeColors?.appBg }}
                                            >
                                                {extractMediaFileName(u) || u?.split('/')?.pop() || ''}
                                            </span>
                                    </Tooltip>}
                                </Box>
                            </Box>))
                        )}
                    </Box>
                </Box> : null}
            </Box>
        </Box>
    )
}

export default ViewExpense;